if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          navLeft: this.querySelector('.nav-btn-left'),
          navRight: this.querySelector('.nav-btn-right'),
        };

        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));

        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });

        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();

        // Attach event listeners to navigation buttons
        this.elements.navLeft?.addEventListener('click', this.navigateBackward.bind(this));
        this.elements.navRight?.addEventListener('click', this.navigateForward.bind(this));

        // Initialize button state
        this.updateNavButtons();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
        this.updateNavButtons();
      }

      /**
       * Navigates to the previous visible media item in the gallery
       * Uses thumbnail visibility to determine navigation order
       * Only moves between thumbnails that are currently visible
       * Updates navigation button states after moving
       * @returns {void}
       */
      navigateBackward() {
        const activeSlide = this.elements.viewer.querySelector('.is-active');
        const allThumbnails = [...this.elements.thumbnails.querySelectorAll('[data-target]')];
        const visibleThumbnails = allThumbnails.filter(thumb => thumb.style.display !== 'none');
        
        // Find current thumbnail index
        const currentThumbIndex = visibleThumbnails.findIndex(
          thumb => thumb.dataset.target === activeSlide.dataset.mediaId
        );

        if (currentThumbIndex > 0) {
          // Get previous visible thumbnail
          const previousThumb = visibleThumbnails[currentThumbIndex - 1];
          this.setActiveMedia(previousThumb.dataset.target, false);
        }

        this.updateNavButtons();
      }

      /**
       * Navigates to the next visible media item in the gallery
       * Uses thumbnail visibility to determine navigation order
       * Only moves between thumbnails that are currently visible
       * Updates navigation button states after moving
       * @returns {void}
       */
      navigateForward() {
        const activeSlide = this.elements.viewer.querySelector('.is-active');
        const allThumbnails = [...this.elements.thumbnails.querySelectorAll('[data-target]')];
        const visibleThumbnails = allThumbnails.filter(thumb => thumb.style.display !== 'none');
        
        // Find current thumbnail index
        const currentThumbIndex = visibleThumbnails.findIndex(
          thumb => thumb.dataset.target === activeSlide.dataset.mediaId
        );

        if (currentThumbIndex < visibleThumbnails.length - 1) {
          // Get next visible thumbnail
          const nextThumb = visibleThumbnails[currentThumbIndex + 1];
          this.setActiveMedia(nextThumb.dataset.target, false);
        }

        this.updateNavButtons();
      }

      /**
       * Updates the state of navigation buttons based on current position
       * Disables back button when at first visible thumbnail
       * Disables forward button when at last visible thumbnail
       * Only considers currently visible thumbnails when determining position
       * @returns {void}
       */
      updateNavButtons() {
        const activeSlide = this.elements.viewer.querySelector('.is-active');
        const allThumbnails = [...this.elements.thumbnails.querySelectorAll('[data-target]')];
        const visibleThumbnails = allThumbnails.filter(thumb => thumb.style.display !== 'none');
        
        const currentThumbIndex = visibleThumbnails.findIndex(
          thumb => thumb.dataset.target === activeSlide.dataset.mediaId
        );

        // Disable back button if we're at the first visible thumbnail
        if (currentThumbIndex <= 0) {
          this.elements.navLeft?.classList.add('disabled');
        } else {
          this.elements.navLeft?.classList.remove('disabled');
        }

        // Disable forward button if we're at the last visible thumbnail
        if (currentThumbIndex >= visibleThumbnails.length - 1) {
          this.elements.navRight?.classList.add('disabled');
        } else {
          this.elements.navRight?.classList.remove('disabled');
        }
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) return;

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);
          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }
          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

          this.preventStickyHeader();
        if (!this.mql.matches || this.elements.thumbnails) {
          activeMedia.parentElement.scrollLeft = activeMedia.offsetLeft;
        }

        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;

        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;
        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;
        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
